import React, { useState, useEffect, useCallback } from 'react';
import { Package2, Users2, X, Plus, Search, Phone, Mail, Building2, Pencil, Trash2, Filter, ChevronDown, Clock, GripVertical, Calendar, ShoppingCart, CheckCircle2, AlertCircle } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { getStockItems, StockItem, createStockItem, deleteStockItem } from '../lib/stockItems';
import { supabase } from '../lib/supabase';
import EmptyState from '../components/EmptyState';
import StockHistoryModal from '../components/StockHistoryModal';
import WithdrawStockModal from '../components/WithdrawStockModal';
import NewStockItemModal from '../components/NewStockItemModal';
import EditStockModal from '../components/EditStockModal';
import ReceiveStockModal from '../components/ReceiveStockModal';
import PlaceOrderModal from '../components/PlaceOrderModal';
import SellableItemsModal from '../components/SellableItemsModal';
import ReceiveItemModal from '../components/ReceiveItemModal';
import SuppliersModal from '../components/SuppliersModal';
import { toast } from '../components/toasts/SimpleToast';
import { getOrders, updateOrderStatus, updateOrderItemStatus, markAllItemsAsReceived, Order, deleteOrder } from '../lib/orders';
import { getSuppliers, Customer } from '../lib/customers';
import PermissionGuard from '../components/PermissionGuard';

function Stock() {
  const { selectedBusiness } = useBusiness();
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  const [isAddingSupplier, setIsAddingSupplier] = useState(false);
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [isReceiveStockModalOpen, setIsReceiveStockModalOpen] = useState(false);
  const [isWithdrawStockModalOpen, setIsWithdrawStockModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isSellableModalOpen, setIsSellableModalOpen] = useState(false);
  const [isPlaceOrderModalOpen, setIsPlaceOrderModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSupplierListModalOpen, setIsSupplierListModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<StockItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [items, setItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [draggedCategory, setDraggedCategory] = useState<string | null>(null);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [isReceiveItemModalOpen, setIsReceiveItemModalOpen] = useState(false);
  const [selectedOrderItem, setSelectedOrderItem] = useState<{
    id: string;
    orderNumber: string;
    name: string;
    unit: string;
    quantity: number;
    receivedQuantity: number;
  } | null>(null);

  const loadStockItems = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const data = await getStockItems(selectedBusiness.id);
      setItems(data);
      setError(null);
    } catch (err) {
      console.error('Error loading stock items:', err);
      setError(err instanceof Error ? err.message : 'Failed to load stock items');
    } finally {
      setLoading(false);
    }
  };

  // Load recent orders from the database
  const loadRecentOrders = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    
    try {
      setLoadingOrders(true);
      const orders = await getOrders(selectedBusiness.id);
      setRecentOrders(orders);
    } catch (err) {
      console.error('Error loading recent orders:', err);
      toast.error({
        title: "Error",
        description: "Failed to load recent orders",
        duration: 3000,
      });
    } finally {
      setLoadingOrders(false);
    }
  }, [selectedBusiness?.id]);

  useEffect(() => {
    loadStockItems();
    loadRecentOrders();
  }, [selectedBusiness?.id, loadRecentOrders]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showMobileActions && !target.closest('.mobile-actions-container')) {
        setShowMobileActions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMobileActions]);

  const handleDragStart = (e: React.DragEvent, type: 'category' | 'item', id: string) => {
    e.stopPropagation();
    if (type === 'category') {
      setDraggedCategory(id);
    } else {
      setDraggedItem(id);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent, targetId: string, type: 'category' | 'item') => {
    e.preventDefault();
    e.stopPropagation();

    if (type === 'category' && draggedCategory && draggedCategory !== targetId) {
      const draggedIndex = filteredCategories.findIndex(c => c.id === draggedCategory);
      const targetIndex = filteredCategories.findIndex(c => c.id === targetId);
      
      if (draggedIndex !== -1 && targetIndex !== -1) {
        try {
          setLoading(true);
          
          // Update order_index for all categories
          const newCategories = [...filteredCategories];
          const [draggedItem] = newCategories.splice(draggedIndex, 1);
          newCategories.splice(targetIndex, 0, draggedItem);
          
          if (!supabase) {
            throw new Error('Database connection not available');
          }
          
          // Create a promise array for updates
          const updatePromises = newCategories.map((category, index) => {
            // Skip if supabase is null (should not happen due to check above)
            if (!supabase) return Promise.resolve();
            
            return supabase
              .from('stock_categories')
              .update({ order_index: index })
              .eq('id', category.id);
          });
          
          await Promise.all(updatePromises);
          await loadStockItems();
        } catch (err) {
          console.error('Error reordering categories:', err);
          setError(err instanceof Error ? err.message : 'Failed to reorder categories');
        } finally {
          setLoading(false);
        }
      }
    }

    setDraggedCategory(null);
    setDraggedItem(null);
  };

  // Check if category has any low stock items
  const getCategoryStockStatus = (items: StockItem[]) => {
    return items.some(item => item.quantity <= item.reorder_point) ? 'low' : 'ok';
  };

  // Group items by category
  const itemsByCategory = React.useMemo(() => {
    const grouped = items.reduce((acc, item) => {
      const categoryId = item.category_id || 'uncategorized';
      if (!acc[categoryId]) {
        acc[categoryId] = {
          id: categoryId,
          name: item.category?.name || 'Uncategorized',
          items: []
        };
      }
      acc[categoryId].items.push(item);
      return acc;
    }, {} as Record<string, { id: string; name: string; items: StockItem[] }>);

    return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  // Filter items based on search query
  const filteredCategories = itemsByCategory.filter(category => {
    // First filter out deleted items
    category.items = category.items.filter(item => !item.is_deleted);
    
    // Return empty categories
    if (category.items.length === 0) {
      return false;
    }
    
    const searchLower = searchQuery.toLowerCase();
    return (
      category.name.toLowerCase().includes(searchLower) ||
      category.items.some(item => 
        item.name.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.sku?.toLowerCase().includes(searchLower)
      )
    );
  });

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!window.confirm('Are you sure you want to delete this item? The stock history for this item will be preserved for auditing purposes.')) {
      return;
    }

    setLoading(true);
    try {
      await deleteStockItem(itemId);
      // Success notification
      toast.success({
        title: "Item deleted",
        description: "The item has been deleted. Stock history has been preserved.",
        duration: 5000,
      });
      await loadStockItems();
      setError(null);
    } catch (err) {
      console.error('Error deleting item:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete item');
      toast.error({
        title: "Error",
        description: err instanceof Error ? err.message : 'Failed to delete item',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to toggle order expansion
  const toggleOrder = (orderId: string) => {
    setExpandedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };
  
  // Function to handle marking an order as received
  const handleMarkAsReceived = async (orderId: string) => {
    try {
      if (!orderId) return;
      
      setLoadingOrders(true);
      
      // Update order status in the database
      await updateOrderStatus(orderId, 'received');
      
      // Reload orders to reflect the changes
      await loadRecentOrders();
      
      // Reload stock items to reflect inventory changes
      await loadStockItems();
      
      toast.success({
        title: "Order Received",
        description: "The order has been marked as received and inventory has been updated.",
        duration: 3000,
      });
    } catch (err) {
      console.error('Error marking order as received:', err);
      toast.error({
        title: "Error",
        description: "Failed to mark order as received",
        duration: 3000,
      });
    } finally {
      setLoadingOrders(false);
    }
  };
  
  // Function to handle cancelling an order
  const handleCancelOrder = async (orderId: string) => {
    try {
      if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
        return;
      }
      
      setLoadingOrders(true);
      
      // Delete the order and its items from the database
      await deleteOrder(orderId);
      
      // Reload orders to reflect the changes
      await loadRecentOrders();
      
      toast.info({
        title: "Order Deleted",
        description: "The order has been deleted.",
        duration: 3000,
      });
    } catch (err) {
      console.error('Error deleting order:', err);
      
      // Show specific error message for orders with received items
      if (err instanceof Error && err.message.includes('Cannot delete order with received items')) {
        toast.error({
          title: "Cannot Delete Order",
          description: "This order has items that have been received. Only orders with no received items can be deleted.",
          duration: 5000,
        });
      } else {
        toast.error({
          title: "Error",
          description: "Failed to delete order",
          duration: 3000,
        });
      }
    } finally {
      setLoadingOrders(false);
    }
  };
  
  // Format date function
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Function to handle receiving individual order items
  const handleReceiveItem = async (itemId: string, orderNumber: string, itemName: string, quantity?: number) => {
    try {
      setLoadingOrders(true);
      
      // If quantity is provided, do a partial receive
      if (quantity !== undefined) {
        // Update order item status
        await updateOrderItemStatus(itemId, 'Partially Received', quantity);
      } else {
        // Full receive
        await updateOrderItemStatus(itemId, 'Received');
      }
      
      // Reload orders to reflect the changes
      await loadRecentOrders();
      
      // Reload stock items to reflect inventory changes
      await loadStockItems();
      
      // Show success notification
      toast.success({
        title: "Item Received",
        description: `${itemName} from Order #${orderNumber} has been marked as received.`,
        duration: 3000,
      });
      
      // Show a separate toast with option to view history
      setTimeout(() => {
        toast.info({
          title: "Stock Updated",
          description: "The item has been added to inventory. View stock history to see details.",
          action: {
            label: "View History",
            onClick: () => setIsHistoryModalOpen(true)
          },
          duration: 5000,
        });
      }, 500);
    } catch (err) {
      console.error('Error receiving item:', err);
      toast.error({
        title: "Error",
        description: "Failed to mark item as received",
        duration: 3000,
      });
    } finally {
      setLoadingOrders(false);
    }
  };

  // Function to open the receive item modal
  const openReceiveItemModal = (item: any, orderNumber: string) => {
    setSelectedOrderItem({
      id: item.id,
      orderNumber: orderNumber,
      name: item.item_name,
      unit: item.item_unit,
      quantity: item.quantity,
      receivedQuantity: item.received_quantity || 0
    });
    setIsReceiveItemModalOpen(true);
  };

  // Function to handle receiving items from the modal
  const handleReceiveFromModal = (quantity: number) => {
    if (!selectedOrderItem) return;
    
    const { id, orderNumber, name } = selectedOrderItem;
    
    if (quantity === selectedOrderItem.quantity - selectedOrderItem.receivedQuantity) {
      // If receiving all remaining, mark as fully received
      handleReceiveItem(id, orderNumber, name);
    } else {
      // Otherwise do a partial receive
      const newTotalReceived = selectedOrderItem.receivedQuantity + quantity;
      handleReceiveItem(id, orderNumber, name, newTotalReceived);
    }
  };

  // Add a new function to handle receiving all items in an order
  const handleReceiveAllItems = async (orderId: string, orderNumber: string) => {
    try {
      if (!orderId) return;
      
      setLoadingOrders(true);
      
      // Mark all items as received
      await markAllItemsAsReceived(orderId);
      
      // Reload orders to reflect the changes
      await loadRecentOrders();
      
      // Reload stock items to reflect inventory changes
      await loadStockItems();
      
      // Show success notification
      toast.success({
        title: "All Items Received",
        description: `All items from Order #${orderNumber} have been marked as received.`,
        duration: 3000,
      });
      
      // Show a separate toast with option to view history
      setTimeout(() => {
        toast.info({
          title: "Inventory Updated",
          description: "All items have been added to inventory. View stock history to see details.",
          action: {
            label: "View History",
            onClick: () => setIsHistoryModalOpen(true)
          },
          duration: 5000,
        });
      }, 500);
    } catch (err) {
      console.error('Error receiving all items:', err);
      toast.error({
        title: "Error",
        description: "Failed to mark all items as received",
        duration: 3000,
      });
    } finally {
      setLoadingOrders(false);
    }
  };

  return (
    <PermissionGuard permission="supplies.pageAccess">
      <div className="space-y-6 pb-24 md:pb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Supplies</h1>
            <p className="hidden sm:block mt-1 text-sm text-gray-500">Manage your inventory and supplies levels</p>
          </div>
          <div className="relative sm:flex sm:flex-row sm:items-center sm:gap-3">
            {/* Mobile Dropdown */}
            <div className="block sm:hidden relative">
              <button
                onClick={() => setShowMobileActions(!showMobileActions)}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Actions
                <ChevronDown className={`ml-1.5 h-3.5 w-3.5 transition-transform duration-200 ${showMobileActions ? 'rotate-180' : ''}`} />
              </button>
              
              {showMobileActions && (
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-10 mobile-actions-container">
                  <button
                    onClick={() => {
                      setShowMobileActions(false);
                      setIsSupplierListModalOpen(true);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                  >
                    <Users2 className="h-3.5 w-3.5 mr-2 text-gray-400" />
                    Suppliers
                  </button>
                  <button
                    onClick={() => {
                      setShowMobileActions(false);
                      setIsHistoryModalOpen(true);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                  >
                    <Clock className="h-3.5 w-3.5 mr-2 text-gray-400" />
                    History
                  </button>
                  <button
                    onClick={() => {
                      setShowMobileActions(false);
                      setIsPlaceOrderModalOpen(true);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                  >
                    <Package2 className="h-3.5 w-3.5 mr-2 text-gray-400" />
                    Place Order
                  </button>
                  <button
                    onClick={() => {
                      setShowMobileActions(false);
                      setIsWithdrawStockModalOpen(true);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                  >
                    <Package2 className="h-3.5 w-3.5 mr-2 text-gray-400" />
                    Withdraw Stock
                  </button>
                  <button
                    onClick={() => {
                      setShowMobileActions(false);
                      setIsReceiveStockModalOpen(true);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                  >
                    <Package2 className="h-3.5 w-3.5 mr-2 text-gray-400" />
                    Receive Stock
                  </button>
                  <button
                    onClick={() => {
                      setShowMobileActions(false);
                      setIsStockModalOpen(true);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                  >
                    <Plus className="h-3.5 w-3.5 mr-2 text-gray-400" />
                    New Stock
                  </button>
                </div>
              )}
            </div>
            
            {/* Desktop Buttons */}
            <div className="hidden sm:flex sm:items-center sm:gap-3">
            <button 
              onClick={() => setIsSupplierListModalOpen(true)}
              className="inline-flex items-center px-2 sm:px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              <Users2 className="h-3.5 w-3.5 mr-1.5" />
              Suppliers
            </button>
            <button 
              onClick={() => setIsHistoryModalOpen(true)}
              className="inline-flex items-center px-2 sm:px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              <Clock className="h-3.5 w-3.5 mr-1.5" />
              History
            </button>
            <button 
              onClick={() => {
                setIsPlaceOrderModalOpen(true);
              }}
              className="inline-flex items-center px-2 sm:px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              <Package2 className="h-3.5 w-3.5 mr-1.5" />
              Place Order
            </button>
            <button 
              onClick={() => setIsWithdrawStockModalOpen(true)}
              className="inline-flex items-center px-2 sm:px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              <Package2 className="h-3.5 w-3.5 mr-1.5" />
              Withdraw Stock
            </button>
            <button 
              onClick={() => setIsReceiveStockModalOpen(true)}
              className="inline-flex items-center px-2 sm:px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              <Package2 className="h-3.5 w-3.5 mr-1.5" />
              Receive Stock
            </button>
            <button 
              onClick={() => setIsStockModalOpen(true)}
              className="inline-flex items-center px-2 sm:px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              New Stock
            </button>
            </div>
          </div>
        </div>

        {/* Categories and Items List */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
          <div className="px-4 py-4 border-b border-gray-100 flex flex-col sm:flex-row gap-3 sm:gap-0 justify-between items-stretch sm:items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search stock items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/40 transition-all duration-200"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-sm text-gray-600">Loading items...</p>
              </div>
            </div>
          ) : error ? (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="p-8 text-center">
              <Package2 className="h-10 w-10 text-gray-400 mx-auto mb-4" />
              <h3 className="text-base font-medium text-gray-900">No items yet</h3>
              <p className="mt-2 text-sm text-gray-500">
                Get started by adding your first inventory item
              </p>
              <button
                onClick={() => setIsStockModalOpen(true)}
                className="mt-5 inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {filteredCategories.map((category) => (
                <div key={category.id} className="relative w-full group">
                  <button
                    onClick={() => toggleCategory(category.id)}
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'category', category.id)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, category.id, 'category')}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                      expandedCategories.has(category.id) ? 'border-blue-200 bg-blue-50/50 shadow-md' : 'border-gray-100 hover:border-blue-100 hover:bg-gray-50/50'
                    } ${
                      getCategoryStockStatus(category.items) === 'low' ? 'bg-red-50/50' : ''
                    } ${draggedCategory === category.id ? 'opacity-50' : ''}`}
                  >
                    <div className="flex flex-col">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center">
                          <button 
                            className="cursor-grab active:cursor-grabbing p-1.5 -ml-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white/80 touch-none transition-colors duration-200"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <GripVertical className="h-4 w-4" />
                          </button>
                          <span className={`text-sm font-medium ${
                            getCategoryStockStatus(category.items) === 'low' ? 'text-red-700' : 'text-gray-900'
                          }`}>
                            {category.name}
                          </span>
                        </div>
                        <ChevronDown 
                          className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${
                            expandedCategories.has(category.id) ? 'rotate-180' : ''
                          }`}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 font-medium">
                          {category.items.length} items
                        </span>
                        {getCategoryStockStatus(category.items) === 'low' && (
                          <span className="text-xs text-red-600 font-medium px-2.5 py-1 bg-red-100/80 rounded-full">
                            Low Stock
                          </span>
                        )}
                      </div>
                    </div>
                  </button>

                  {expandedCategories.has(category.id) && (
                    <div className="mt-3 space-y-3">
                      {category.items.map((item) => (
                        <div key={item.id} className={`p-4 bg-white rounded-xl border border-gray-100 hover:border-blue-100 shadow-sm hover:shadow-md transition-all duration-200 ${
                          item.quantity <= item.reorder_point ? 'bg-red-50/30' : ''
                        }`}>
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-gray-900 truncate">{item.name}</span>
                              </div>
                              {item.description && (
                                <div className="text-xs text-gray-500 mt-1.5 truncate">{item.description}</div>
                              )}
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3">
                                <span className="inline-flex items-center text-xs font-medium text-gray-600 flex-shrink-0">
                                  <Package2 className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                                  {item.quantity} {item.unit}
                                </span>
                                {
                                  item.quantity <= item.reorder_point && (
                                    <span className="text-red-600 font-medium text-xs whitespace-nowrap bg-red-100/50 px-2 py-0.5 rounded-full flex-shrink-0">
                                      Below reorder point ({item.reorder_point})
                                    </span>
                                  )
                                }
                              </div>
                            </div>
                            <div className="flex items-center gap-4 flex-shrink-0">
                              <div className="flex items-center gap-1 ml-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedItem(item);
                                    setIsEditModalOpen(true);
                                  }}
                                  className="p-2 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors duration-200"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteItem(item.id);
                                  }}
                                  className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors duration-200"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Orders Section */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
          <div className="border-b border-gray-100 pb-4 flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Recent Orders</h2>
              <p className="text-sm text-gray-500 mt-1">View and manage your recent supplier orders</p>
            </div>
            <button 
              onClick={loadRecentOrders}
              className="text-xs text-blue-600 hover:text-blue-800 focus:outline-none"
            >
              Refresh
            </button>
          </div>
          
          {loadingOrders ? (
            <div className="py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-sm text-gray-600">Loading orders...</p>
              </div>
            </div>
          ) : recentOrders.length === 0 ? (
            <div className="py-8">
              <div className="text-center">
                <Package2 className="h-10 w-10 text-gray-400 mx-auto mb-4" />
                <h3 className="text-base font-medium text-gray-900">No recent orders</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Your recent orders will appear here after you place orders with suppliers
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              {recentOrders.map(order => (
                <div key={order.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div 
                    className={`p-4 ${order.status === 'received' ? 'bg-green-50' : order.status === 'cancelled' ? 'bg-red-50' : 'bg-gray-50'} flex items-center justify-between cursor-pointer`}
                    onClick={() => toggleOrder(order.id || '')}
                  >
                    <div>
                      <div className="flex items-center">
                        <h3 className="text-sm font-medium text-gray-900">
                          {order.supplier?.first_name} {order.supplier?.last_name}
                          {order.supplier?.email && (
                            <span className="text-gray-500 ml-2">
                              ({order.supplier.email})
                            </span>
                          )}
                        </h3>
                        <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {order.order_number}
                        </span>
                        <span 
                          className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium 
                            ${order.status === 'pending' 
                              ? 'bg-yellow-100 text-yellow-800' 
                              : order.status === 'received' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}
                        >
                          {order.status === 'pending' ? 'Pending' : order.status === 'received' ? 'Received' : 'Cancelled'}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-gray-500 flex items-center">
                        <Calendar className="h-3 w-3 mr-1.5" />
                        {formatDate(order.created_at || '')} • {order.items?.length || 0} items • Total: ${order.total_amount.toFixed(2)}
                      </div>
                    </div>
                    <div className="flex items-center">
                      {order.status === 'pending' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (order.id && order.order_number) {
                              handleReceiveAllItems(order.id, order.order_number);
                            }
                          }}
                          className="mr-3 px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <CheckCircle2 className="h-3 w-3 inline-block mr-1" />
                          Receive All
                        </button>
                      )}
                      {order.status === 'pending' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (order.id) handleCancelOrder(order.id);
                          }}
                          className="mr-3 px-2 py-1 text-xs font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          <Trash2 className="h-3 w-3 inline-block mr-1" />
                          Delete
                        </button>
                      )}
                      <ChevronDown
                        className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                          expandedOrders.has(order.id || '') ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </div>
                  
                  {expandedOrders.has(order.id || '') && order.items && (
                    <div className="divide-y divide-gray-100">
                      {order.items.map((item) => (
                        <div key={item.id} className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center">
                                <h4 className="text-sm font-medium text-gray-900">{item.item_name}</h4>
                                {item.status === 'Not Received' ? (
                                  <span className="ml-2 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                                    {item.quantity} {item.quantity > 1 ? 'units' : 'unit'} not received
                                  </span>
                                ) : item.status === 'Partially Received' ? (
                                  <span className="ml-2 text-xs text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded-full">
                                    {item.received_quantity}/{item.quantity} units received
                                  </span>
                                ) : (
                                  <span className="ml-2 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                                    All units received
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <div className="text-sm font-medium text-gray-900">
                                  {item.quantity} {item.item_unit}{item.quantity > 1 ? 's' : ''}
                                </div>
                                <div className="text-xs text-gray-500">
                                  ${item.unit_price.toFixed(2)} each • ${item.total_price.toFixed(2)} total
                                </div>
                              </div>
                              {order.status === 'pending' && ( 
                                item.status === 'Received' ? (
                                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <CheckCircle2 className="h-3 w-3 mr-1" />
                                    Received
                                  </span>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    {item.status === 'Partially Received' && (
                                      <div className="flex items-center gap-1">
                                        <span className="text-xs text-gray-500">
                                          {item.received_quantity}/{item.quantity}
                                        </span>
                                      </div>
                                    )}
                                    <button
                                      onClick={() => {
                                        if (item.id && order.order_number) {
                                          if (item.quantity === 1) {
                                            // If there's only one unit, receive it directly
                                            handleReceiveItem(item.id, order.order_number, item.item_name);
                                          } else {
                                            // For multiple units, open the receive modal
                                            openReceiveItemModal(item, order.order_number);
                                          }
                                        }
                                      }}
                                      className="px-2 py-1 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                    >
                                      <CheckCircle2 className="h-3 w-3 inline-block mr-1" />
                                      Receive{item.status === 'Partially Received' ? ` (${item.quantity - (item.received_quantity || 0)} left)` : ''}
                                    </button>
                                  </div>
                                )
                              )}
                              {order.status !== 'pending' && item.status === 'Received' && (
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Received
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="p-4 bg-gray-50">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-600">Total</span>
                          <span className="text-sm font-medium text-gray-900">${order.total_amount.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <NewStockItemModal
          isOpen={isStockModalOpen}
          onClose={() => setIsStockModalOpen(false)}
          onSuccess={() => {
            setIsStockModalOpen(false);
            loadStockItems();
          }}
        />

        <ReceiveStockModal
          isOpen={isReceiveStockModalOpen}
          onClose={() => setIsReceiveStockModalOpen(false)}
          onSuccess={() => {
            setIsReceiveStockModalOpen(false);
            loadStockItems();
          }}
        />

        <WithdrawStockModal
          isOpen={isWithdrawStockModalOpen}
          onClose={() => setIsWithdrawStockModalOpen(false)}
          onSuccess={() => {
            setIsWithdrawStockModalOpen(false);
            loadStockItems();
          }}
        />

        <StockHistoryModal
          isOpen={isHistoryModalOpen}
          onClose={() => setIsHistoryModalOpen(false)}
        />

        <SellableItemsModal
          isOpen={isSellableModalOpen}
          onClose={() => setIsSellableModalOpen(false)}
          onUpdate={loadStockItems}
          businessId={selectedBusiness?.id}
        />

        <PlaceOrderModal
          isOpen={isPlaceOrderModalOpen}
          onClose={() => {
            setIsPlaceOrderModalOpen(false);
            // Refresh orders after modal closes
            loadRecentOrders();
          }}
          items={items}
        />

        <EditStockModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedItem(null);
          }}
          onSuccess={() => {
            setIsEditModalOpen(false);
            setSelectedItem(null);
            loadStockItems();
          }}
          item={selectedItem}
        />

        {/* Receive Item Modal */}
        {selectedOrderItem && (
          <ReceiveItemModal
            isOpen={isReceiveItemModalOpen}
            onClose={() => setIsReceiveItemModalOpen(false)}
            itemName={selectedOrderItem.name}
            itemUnit={selectedOrderItem.unit}
            totalQuantity={selectedOrderItem.quantity}
            remainingQuantity={selectedOrderItem.quantity - selectedOrderItem.receivedQuantity}
            onReceive={handleReceiveFromModal}
          />
        )}

        {/* Add the SupplierListModal */}
        <SuppliersModal
          isOpen={isSupplierListModalOpen}
          onClose={() => setIsSupplierListModalOpen(false)}
        />
      </div>
    </PermissionGuard>
  );
}

export default Stock;